import { observer } from 'mobx-react-lite';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAsync } from 'react-use';

import { Messages, Spinner } from '@ifs-gcp/components';
import {
  type AuditCreation,
  AuditFactory,
  type IAppContextProvider,
  type IAudit,
  type ITemplateNode,
  type Standards,
} from '@ifs-gcp/core';
import type { AuditComposeChild } from '@ifs-gcp/core/models';

import { TEMPLATES_MAP } from '@/constants/templates';
import { ifsLoaders } from '@/hooks/useJsonTemplate';
import { getTranslation } from '@/utils/translations';

import type { StepCompleteData, StepStatus } from '../../dashboard-modal';
import styles from './audit-compose.module.scss';
import { syncChecklistLanguage } from './syncChecklistLanguage';

/**
 * There is a minimum audit preparation time of two seconds to give
 * user false sense of creation. If audit is prepared in less than two seconds
 * we still have to show the spinner
 */
const COMPOSE_MIN_DURATION = 2000;

interface AuditComposeProps {
  element: ITemplateNode<AuditComposeChild>;
  domain: AuditCreation;
  status: StepStatus;
  standards: Standards;
  userProvider: IAppContextProvider;
  onComplete: (data: StepCompleteData) => void;
}

export const AuditComposer = observer(
  ({ element, domain, status, standards, userProvider, onComplete }: AuditComposeProps) => {
    const {
      t,
      i18n: { language },
    } = useTranslation();

    const {
      props: { text },
    } = element;

    useAsync(async () => {
      try {
        const [audit] = await Promise.all([
          (async function (): Promise<IAudit> {
            const composeHandler = domain.factory(element.props.id, element);
            const auditFileContent = await composeHandler.compose(ifsLoaders, TEMPLATES_MAP);

            const auditFactory = new AuditFactory(
              ifsLoaders,
              TEMPLATES_MAP,
              auditFileContent.data.modules,
            );

            const audit = await auditFactory.create(auditFileContent, standards, userProvider);
            // IA2-3845: checklist language should use app language by default,
            // but only for new OSA audits
            syncChecklistLanguage(audit, userProvider.language);

            return audit;
          })(),
          new Promise((resolve) => setTimeout(resolve, COMPOSE_MIN_DURATION)),
        ]);

        onComplete({
          status: 'success',
          details: {
            audit,
          },
        });
      } catch {
        onComplete({
          status: 'error',
        });
      }
    });

    const statusContent: Record<StepStatus, React.ReactNode> = {
      loading: (
        <React.Fragment>
          <Spinner />

          <p className={styles.paragraph}>{getTranslation(text, language)}</p>
        </React.Fragment>
      ),
      complete: (
        <Messages
          variant="plain"
          type="success-box"
          headerText={t('common.success')}
          messages={[t('dashboard.start_modal.audit_compose_success')]}
        />
      ),
      incomplete: (
        <Messages
          variant="plain"
          type="error-box"
          headerText={t('common.error')}
          messages={[t('common.something_went_wrong')]}
        />
      ),
    };

    return <div className={styles.wrapper}>{statusContent[status]}</div>;
  },
);
